import { useMemo, useCallback } from "react";
import { useLogs } from "../context/LogsContext";

export const useAnswerLogsPagination = ({
  effectivePagination,
  isSearching,
  currentSearchPageRef,
  getCurrentSearchQuery,
  searchMode,
}) => {
  const {
    queryCache,
    generateCacheKey,
    searchAnswers,
    fetchAnswersForPage,
    isLoadingAnswers,
  } = useLogs();

  // Calculate total pages
  const totalPages = useMemo(
    () => Math.ceil(effectivePagination.total / effectivePagination.limit) || 1,
    [effectivePagination.total, effectivePagination.limit]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage > 0 && newPage <= totalPages) {
        if (isSearching) {
          // Update our tracking ref
          currentSearchPageRef.current = newPage;

          const currentQuery = getCurrentSearchQuery();

          // Check cache for search results on this page
          let formattedQuery = currentQuery;
          if (searchMode === "organization" && currentQuery) {
            formattedQuery = parseInt(currentQuery, 10);
          }

          const cacheKey = generateCacheKey(
            newPage,
            effectivePagination.limit,
            `${searchMode}:${formattedQuery}`
          );

          const searchParams = {};
          if (searchMode === "answerId") {
            searchParams.search = currentQuery;
          } else if (searchMode === "email") {
            searchParams.email = currentQuery;
          } else if (searchMode === "organization") {
            searchParams.orgid = parseInt(currentQuery, 10);
          }

          // Pass a flag indicating whether the data is already cached
          // The searchAnswers function can use this to avoid unnecessary API calls
          searchAnswers(
            searchParams,
            newPage,
            effectivePagination.limit,
            !!queryCache[cacheKey]
          );
        } else {
          // Check cache for regular page fetch
          const cacheKey = generateCacheKey(newPage, effectivePagination.limit);
          fetchAnswersForPage(newPage, !!queryCache[cacheKey]);
        }
      }
    },
    [
      totalPages,
      isSearching,
      currentSearchPageRef,
      getCurrentSearchQuery,
      searchMode,
      generateCacheKey,
      effectivePagination.limit,
      searchAnswers,
      queryCache,
      fetchAnswersForPage,
    ]
  );

  // Create pagination button elements
  const pageButtons = useMemo(() => {
    const currentPage = effectivePagination.page;
    const buttons = [];

    // Add previous button
    buttons.push({
      key: "prev",
      onClick: () => handlePageChange(currentPage - 1),
      disabled: currentPage === 1 || isLoadingAnswers,
      label: "«",
      type: "prev",
    });

    // Calculate range of page numbers to display
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    // Adjust if we're near the end
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      buttons.push({
        key: 1,
        onClick: () => handlePageChange(1),
        active: currentPage === 1,
        disabled: isLoadingAnswers,
        label: "1",
        type: "page",
      });

      if (startPage > 2) {
        buttons.push({
          key: "start-ellipsis",
          label: "...",
          type: "ellipsis",
        });
      }
    }

    // Add page number buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push({
        key: i,
        onClick: () => handlePageChange(i),
        active: currentPage === i,
        disabled: isLoadingAnswers,
        label: i.toString(),
        type: "page",
      });
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push({
          key: "end-ellipsis",
          label: "...",
          type: "ellipsis",
        });
      }

      buttons.push({
        key: totalPages,
        onClick: () => handlePageChange(totalPages),
        active: currentPage === totalPages,
        disabled: isLoadingAnswers,
        label: totalPages.toString(),
        type: "page",
      });
    }

    // Add next button
    buttons.push({
      key: "next",
      onClick: () => handlePageChange(currentPage + 1),
      disabled: currentPage === totalPages || isLoadingAnswers,
      label: "»",
      type: "next",
    });

    return buttons;
  }, [
    effectivePagination.page,
    totalPages,
    handlePageChange,
    isLoadingAnswers,
  ]);

  return {
    totalPages,
    currentPage: effectivePagination.page,
    pageButtons,
    handlePageChange,
    showPagination: effectivePagination.total > 0,
  };
};
